/* eslint-disable import/no-cycle */
import CouponDTO from '../types/Coupon';
import { ReferralDTO } from '../types/Referral';
import EventDTO, { EventAudienceDiscountDTO } from './event';
import { EventSeatDTO } from './event-seat';
import { EventTransactionSummaryDTO } from './event-transaction';
import { PaymentDTO } from './payment';
import TicketDTO from './ticket';
import { UserBusiness, UserEvent } from './user';
import type { LastRejectionDTO, OpenPaymentDTO } from './checkout-session';

export enum PendingOrderStatus {
  no_reminder = 'no_reminder',
  purchased_without_reminder = 'purchased_without_reminder',
  first_reminder = 'first_reminder',
  second_reminder = 'second_reminder',
  third_reminder = 'third_reminder',
  purchased_after_first = 'purchased_after_first',
  purchased_after_second = 'purchased_after_second',
  purchased_after_third = 'purchased_after_third',
  did_not_purchase = 'did_not_purchase',
}

export interface ItemCustomFieldValue {
  field_id: string;
  label: string;
  value: string;
}

export interface ItemDTO {
  id: string;
  name: string;
  last_name: string;
  person_id: string;
  custom_fields?: ItemCustomFieldValue[];
  ticket: TicketDTO;
  event_seat?: EventSeatDTO;
  // Person N of a table sold whole (1..seat_count); null for a single seat.
  seat_index?: number | null;
  audience_discount?: EventAudienceDiscountDTO;
  audience_discount_id?: string;
  used_at: string | null;
  attendance_arrival?: string | null;
  attendance_exit?: string | null;
  /** Last presence-only door that recorded this item; meaningful when the assigned scanner door has `uses_qr === false`. */
  last_door_scanned_at_id?: string | null;
  display_id?: string;
  /** A pre-printed ticket. On an order it means the sold ticket was linked to one. */
  is_printed?: boolean;
  // UUID del ítem: es el link para compartir solo esta entrada (/entrada/:custom_id).
  custom_id?: string | null;
}

/**
 * The answer to linking or unlinking a printed ticket: the order's item that
 * took the old one's place, bare or wrapped as `{ item }`. Read it with
 * `getPrintedLinkItem` (src/utils/printedTicketLink.ts).
 */
export type PrintedTicketLinkDTO = ItemDTO | { item: ItemDTO };

// Estado del último mail de la entrada (lo informa SendGrid por webhook; ver
// utils/getEmailDeliveryMeta).
export type EmailDeliveryStatus = 'sending' | 'sent' | 'deferred' | 'delivered' | 'bounced' | 'blocked' | 'dropped' | 'failed';
export type EmailDeliveryReason = 'mailbox_full' | 'invalid_address' | 'rejected' | 'unsubscribed' | 'send_error' | 'other';

export interface OrderDTO {
  id: string;
  user_id?: number;
  event_id: string;
  status: 'paid' | 'pending' | 'rejected',
  email: string;
  phone?: string;
  instagram_user?: string;
  items: ItemDTO[];
  is_manual: boolean;
  payments: PaymentDTO[];
  event: EventDTO;
  display_id: string;
  custom_id: string;
  created_at: string;
  coupon_id: string;
  coupon: CouponDTO;
  owner_referral: ReferralDTO | null;
  // Pending orders with a hold: until when the buyer can pay before the
  // places can go to someone else. Only sent by GET /orders/custom-id.
  payment_deadline?: string | null;
  // Checkout nuevo: 2 = la orden salió del checkout nuevo (pantallas de resultado nuevas).
  checkout_version?: number | null;
  // El pago pendiente que todavía se puede completar (link o CVU vivo).
  open_payment?: OpenPaymentDTO | null;
  // Por qué no se pudo cobrar el último intento, en castellano para el comprador.
  last_rejection?: LastRejectionDTO | null;
  pending_status: PendingOrderStatus;
  // Mail de la entrada: si salió y qué pasó después. Solo llega a la productora
  // y a root; null en órdenes anteriores al seguimiento.
  email_sent?: boolean | null;
  email_delivery_status?: EmailDeliveryStatus | null;
  email_delivery_reason?: EmailDeliveryReason | null;
  email_delivery_detail?: string | null;
  email_delivery_updated_at?: string | null;
}

export interface OrderItemData {
  name: string;
  last_name: string;
  person_id: string;
  ticket_id: string;
  ticket: TicketDTO;
  ticket_extra: boolean;
  seat_name?: string;
  seat_id?: string;
  audience_discount?: EventAudienceDiscountDTO;
  audience_discount_id?: string;
  custom_fields?: ItemCustomFieldValue[];
}

export interface TicketSummaryDTO {
  ticket_name: string;
  ticket_id: string;
  ticket_price: number;
  count: number;
  total: number;
  count_manual: number;
  count_online: number;
  total_manual: number;
  total_online: number;
  scanned: number;
}

export interface SellerSummaryDTO {
  seller_id: string;
  seller_name: string;
  tickets_summary: TicketSummaryDTO[];
  manual_items_count: number;
  manual_amount: number;
  manual_comission_amount: number;
  referral_count: number;
  referral_amount: number;
  referral_comission_amount: number;
  total_items_count: number;
  total_amount: number;
  total_comission_amount: number;
  service_charge_total: number;
  discounts_total: number;
  tickets_total: number;
  seller_orders: OrderDTO[];
  transactions_summary: EventTransactionSummaryDTO;
  balance: number;
  referral: ReferralDTO | null;
  items_scanned: number;
  seller_qr_count: number;
  user: {
    current_event: UserEvent;
    current_business: UserBusiness;
    email: string;
  };
}

export type SellerSimpleSummaryDTO = {
  user_id: string;
  user_event_id: string | null;
  username: string;
  email: string;
  active: boolean;
  permissions: string;
  alias: string | null;
  tag: string | null;
  group: string | null;
  total_items_count: number;
  items_scanned: number;
  seller_qr_count: number;
  total_amount_online: number;
  total_amount_referral: number;
  total_amount: number;
  commission: number;
  total_commision: number;
  expenses: number;
  balance: number;
};

export interface OrderTotalsDTO {
  total_amount: number;
  subtotal: number;
  tickets_total: number;
  discounts_total: number;
  service_charge_fee: number;
  payment_method_discounts: number;
  base_tickets_total: number;
}
